<script setup lang="ts">
import { isNumber } from '@vueuse/core'
import logBar, { type Series } from '@/charts/country/log-bar'
import pictorialBar from '@/charts/country/pictorial-bar'
import type { Values } from '@/composables/useValues'

const props = defineProps<{
    dimension: Dimension
    country: Country
    data: Values
}>()

/**
 * Translations.
 */
const { t } = useI18n()

const { config } = useParams()

/**
 * The custom option for the log series.
 */

const logSeriesOption = computed(
    () =>
        ({
            formatter: props.data.getValueFormatter(),
            max: 1000,
            maxDisplay: 20,
            name: t('per-thousand-people'),
            type: 'log',
            valueKey: 'score'
        } satisfies Series)
)

/**
 * The custom option for the absolute series.
 */
const absoluteSeriesOption = {
    formatter: props.data.getValueFormatter(true),
    max: undefined,
    maxDisplay: undefined,
    name: t('absolute-estimated-number'),
    type: 'value',
    valueKey: 'value'
} satisfies Series

/**
 * Log chart container
 */
const logContainer = ref<HTMLDivElement>()

/**
 * Size of the log container.
 */
const logSize = reactive(useElementSize(logContainer))

/**
 * Log chart options.
 */
const logChartOptions = computed(() => {
    return logBar(
        props.data,
        [absoluteSeriesOption, unref(logSeriesOption)],
        logSize
    )
})

/**
 * Instance for the log chart.
 */
const { instance: logInstance } = useChart(logContainer, logChartOptions)

/**
 * Pictorial chart container
 */
const pictorialContainer = ref<HTMLDivElement>()

/**
 * Instance for the pictorial chart.
 */
const { instance: pictorialInstance } = useChart(pictorialContainer, () => {
    return pictorialBar(
        props.data,
        props.data.isAbsolute ? absoluteSeriesOption : unref(logSeriesOption)
    )
})

const comparionTitle = computed(() => {
    return `${t('comparison')} (${
        config.prevalenceUnit === 'absolute'
            ? t('absolute-estimated-number')
            : t('per-thousand-people')
    })`
})

/**
 * Check if there is no data.
 */
const noData = computed(() => {
    return !isNumber(props.data.countryData?.value)
})
</script>

<template>
    <CountryCard
        class="col-span-2"
        :dimension="dimension"
        :country="country"
        :instance="pictorialInstance"
        :title="comparionTitle"
    >
        <UiMessage v-if="noData">
            {{ t('no-data-available') }}
        </UiMessage>
        <div v-else ref="pictorialContainer" class="h-full" />
    </CountryCard>

    <CountryCard
        :dimension="dimension"
        :country="country"
        :title="country?.name"
        :instance="logInstance"
    >
        <UiMessage v-if="noData">
            {{ t('no-data-available') }}
        </UiMessage>
        <div v-else ref="logContainer" class="h-full" />
    </CountryCard>
</template>